import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";

gsap.registerPlugin(ScrollTrigger);

const FeaturesSection = () => {
  const sectionRef = useRef(null);
  const featuresRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const features = featuresRef.current?.querySelectorAll(".feature-card");

    if (section && features) {
      features.forEach((feature, _index) => {
        gsap.fromTo(
          feature,
          { y: 100, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1,
            scrollTrigger: {
              trigger: feature,
              start: "top bottom-=100",
              end: "bottom center",
              scrub: 1,
              toggleActions: "play none none reverse",
            },
          }
        );
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [featuresRef, sectionRef]);

  const features = [
    {
      icon: "🚀",
      title: "Smart Matching",
      description:
        "AI-powered matching system connects students with the perfect internship opportunities based on skills and interests.",
    },
    {
      icon: "📊",
      title: "Real-time Analytics",
      description:
        "Comprehensive dashboards provide insights into application status, performance metrics, and feedback.",
    },
    {
      icon: "🔐",
      title: "Secure Assessments",
      description:
        "Conduct and complete internship assessments in a secure, monitored environment with anti-cheating measures.",
    },
    {
      icon: "🌐",
      title: "Global Opportunities",
      description:
        "Access internships from leading companies around the world, all from a single unified platform.",
    },
  ];

  return (
    <section
      id="features"
      ref={sectionRef}
      className="min-h-screen bg-gradient-to-b from-gray-900 to-black py-20 px-4"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="container mx-auto text-center mb-16"
      >
        <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
          Next-Gen Internship Experience
        </h2>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
          The GUC Internship Portal reimagines how students connect with
          companies through cutting-edge technology and streamlined processes.
        </p>
      </motion.div>

      <div
        ref={featuresRef}
        className="container mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
      >
        {features.map((feature, index) => (
          <motion.div
            key={index}
            className="feature-card bg-gray-800 bg-opacity-50 backdrop-blur-lg rounded-2xl p-6 border border-gray-700 hover:border-purple-500 transition-all hover:shadow-lg hover:shadow-purple-500/20"
            whileHover={{ y: -5 }}
          >
            <div className="text-4xl mb-4">{feature.icon}</div>
            <h3 className="text-xl font-bold text-white mb-3">
              {feature.title}
            </h3>
            <p className="text-gray-400">{feature.description}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default FeaturesSection;
